import React, { useState, useRef, useEffect } from 'react';
import { FaRegStopCircle } from "react-icons/fa";
import { FaCircleArrowUp } from "react-icons/fa6";
import { ChatInputProps } from '../../types/types';
import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";

const ChatInput: React.FC<ChatInputProps> = ({
  input,
  setInput,
  onSendMessage,
  configData,
  loading,
  pendingMsgCount,
  allowSendMsg,
  messages,
  activeChatId,
  isSidebarOpen
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [animate, setAnimate] = useState(false);
  const [linkRequestModal, setIsLinkRequestModalOpen] = useState(false);

  const app_ai_settings = configData && configData?.app_ai_settings;

  const [prompts, setPrompts] = useState(() => {
    const {
      ai_suggestedprompt1,
      ai_suggestedprompt2,
      ai_suggestedprompt3,
      ai_suggestedprompt4,
    } = configData.app_ai_settings;

    const promptList = [
      ai_suggestedprompt1,
      ai_suggestedprompt2,
      ai_suggestedprompt3,
      ai_suggestedprompt4,
    ].filter((p) => p && p.trim() !== "");

    return promptList;
  });

  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const isLight = isLightColor(backgroundColor);

  const lighterBackground = isLight
    ? darkenColor(backgroundColor, 3)
    : lightenColor(backgroundColor, 5);

  const lighterInputBoxBackground = isLight
    ? darkenColor(backgroundColor, 7)
    : lightenColor(backgroundColor, 7);

  useEffect(() => {
    if (app_ai_settings.ai_showsuggestedprompts === "true") {
      setAnimate(true);
    }
  }, []);

  useEffect(() => {
    setAnimate(false);
    const timeout = setTimeout(() => {
      setAnimate(true);
    }, 50);
    return () => clearTimeout(timeout);
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (pendingMsgCount <= 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        const textArea = e.currentTarget;
        const { selectionStart, selectionEnd } = textArea;

        const newText =
          input.substring(0, selectionStart) +
          "\n" +
          input.substring(selectionEnd);

        setInput(newText);

        setTimeout(() => {
          textArea.selectionStart = textArea.selectionEnd = selectionStart + 1;
        }, 0);
      } else {
        e.preventDefault();
        onSendMessage(input);
        if (inputRef.current) {
          inputRef.current.style.height = 'auto';
        }
      }
    }
  };

  const handlePromptClick = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
    onSendMessage(prompt);
  };

  const isMobile = window.innerWidth <= 640;

  return (
    <div
      className={`flex flex-col w-full max-w-[96%] ${
        messages.length === 0
          ? isSidebarOpen
            ? "mx-auto xl:w-[51%] lg:w-[55%] md:w-[60%] w-[90%] mt-12"
            : "mx-auto lg:w-[80%] xl:w-[51%] md:w-[95%] w-[90%] mt-12"
          : "sticky bottom-0 mx-auto lg:w-[80%] md:w-[95%] w-full px-4 pb-[3%] mt-[5%] md:mt-[8%]"
      }`}
    >
      {String(allowSendMsg) === "false" && (
        <div 
          className="w-full text-center py-3 px-4 rounded-lg flex items-center justify-center"
          style={{ 
            background: lighterInputBoxBackground,
            fontWeight: "500",
            fontSize: "14px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}
        >
          <span className="text-red-500">Maximum permitted message quota exceeded for this month please contact course owner to increase the quota</span>
          {linkRequestModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
                <h2 className="text-lg font-semibold mb-4">Coming Soon</h2>
                <p>This feature will be available shortly. Stay tuned!</p>
                <button
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() => setIsLinkRequestModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="relative w-full">
        <textarea
          ref={inputRef}
          className="w-full py-3 px-4 pr-12 rounded-[1.5rem] border-none gray-out-btn focus:outline-none resize-none text-white overflow-hidden flex items-center h-12 max-h-30"
          value={input}
          style={{ 
            color: configData.app_ui_settings.ui_fontcolor, 
            background: backgroundColor,
            lineHeight: '1.5',
            border: `1px solid ${lighterBackground}`,
            boxShadow:'0 2px 6px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.15)',
                  
          }}
          onChange={(e) => setInput(e.target.value)}
          placeholder={"Ask me something..."}
          onKeyDown={handleKeyDown}
          onInput={(e) => {
            const textArea = e.target as HTMLTextAreaElement;
            textArea.style.height = '48px'; 
            textArea.style.height = `${Math.min(textArea.scrollHeight, 120)}px`;
          }}
        />

        <button
          onClick={() => {
            if (loading) {
              // Handle stop functionality here if needed
            } else if (pendingMsgCount > 0) {
              onSendMessage(input);
            }
          }} 
          className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
            (input.trim() === "" || pendingMsgCount <= 0) && !loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"} p-2`}
          disabled={input.trim() === "" && !loading}
          style={{
            display: 'inline-block',
            padding: '8px',
            borderRadius: '50%',
          }}
        >
          {loading ? (
            <FaRegStopCircle
              className="h-6 w-6 opacity-50 cursor-not-allowed"
              style={{ color: configData.app_ui_settings.ui_fontcolor }}
            />
          ) : (
            <FaCircleArrowUp
              className="h-6 w-6"
              style={{ color: configData.app_ui_settings.ui_fontcolor }}
            />
          )}
        </button>   
      </div>

      {String(app_ai_settings.ai_showsuggestedprompts) === "true" && messages.length === 0 && String(allowSendMsg) === "true" && (
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-6 w-full mt-10 ${animate ? 'fadeInAnimation' : ''}`}
          style={{
            maxWidth: '100%',
            alignItems: 'stretch',
          }}
        >
          {prompts.map((prompt, index) => (
            <div
              key={index}
              className="w-full"
              style={{
                minHeight: '80px',
                maxHeight: '120px',
              }}
            >
              <button
                onClick={() => handlePromptClick(prompt)}
                className={`text-black-600 rounded-[1.5rem] flex items-center justify-center px-4 py-4 w-full h-full transition-all duration-300 ease-in-out transform hover:translate-y-[-2px] hover:shadow-lg  ml-0`}
                style={{
                  color: configData.app_ui_settings.ui_fontcolor,
                  background: lighterBackground,
                  fontSize: '14px',
                  fontWeight: '400',
                  textAlign: 'center',
                  lineHeight: '1.4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '80px',
                  maxHeight: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordWrap: 'break-word',
                  hyphens: 'auto',
                }}
              >
                <span className="text-center leading-tight">
                  {prompt}
                </span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatInput;